// src/services/firebaseService.ts
import type { User } from 'firebase/auth';
import type { Message } from '@/lib/firebase';
import FirebaseService from '@/lib/firebase';

export interface MessageData {
  sender: 'user' | 'assistant' | 'system';
  content: string;
  userName: string;
  userEmail?: string;
  photoURL?: string;
  read: boolean;
  traceId: string;
}

export class EnhancedFirebaseService {
  /**
   * Subscribe to Firestore messages for an assistant
   */
  static onMessages(
    assistantId: string,
    callback: (messages: Message[]) => void
  ): () => void {
    if (!assistantId) {
      callback([]);
      return () => {};
    }

    return FirebaseService.onMessages(assistantId, callback);
  }

  /**
   * Save a user message to Firestore
   */
  static async saveMessage(
    assistantId: string,
    messageData: MessageData
  ): Promise<void> {
    if (!assistantId) {
      throw new Error('Assistant ID is required');
    }

    const firestoreMessage: Omit<Message, 'id' | 'timestamp'> = {
      sender: messageData.sender,
      content: messageData.content,
      userName: messageData.userName,
      userEmail: messageData.userEmail || '',
      photoURL: messageData.photoURL,
      read: messageData.read,
      traceId: messageData.traceId
    };

    await FirebaseService.addMessage(assistantId, firestoreMessage);
  }

  /**
   * Update assistant usage statistics
   */
  static async updateUsageStats(assistantId: string): Promise<void> {
    try {
      await FirebaseService.incrementAssistantUsage(assistantId);
    } catch (error) {
      console.warn("Could not update assistant usage:", error);
    }
  }

  /**
   * Create a user message payload for Firestore
   */
  static createUserMessagePayload(
    text: string,
    currentUser: User,
    traceId: string
  ): MessageData {
    return {
      sender: 'user',
      content: text,
      userName: currentUser.displayName || 'Anonymous',
      userEmail: currentUser.email || undefined,
      photoURL: currentUser.photoURL || undefined,
      read: false,
      traceId
    };
  }

  /**
   * Get the latest assistant message from Firestore
   * This is useful for fetching the complete message with metadata after streaming
   */
  static async getLatestAssistantMessage(
    assistantId: string
  ): Promise<Message | null> {
    try {
      // Get the last 5 messages to find the latest assistant message
      const messages = await FirebaseService.getMessages(assistantId, 5);
      
      // Find the most recent assistant message
      const latestAssistantMessage = messages.find(msg => msg.sender === 'assistant');
      
      if (latestAssistantMessage) {
        console.log('Found latest assistant message with structured output:', 
          latestAssistantMessage.structuredOutput ? 'Yes' : 'No');
      }
      
      return latestAssistantMessage || null;
    } catch (error) {
      console.error('Error fetching latest assistant message:', error);
      return null;
    }
  }
}